import { ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../utils';
import { ContentType } from '../../../types/contents';
import type { StudentBlock, StudentContent, StudentExerciseContent } from '../hooks/use-class-blocks';
import type { LatestSubmissionResult, ExerciseSubmissionRecord } from '../types/exercise';
import type { RenderContentOptions } from '../hooks/content-renderer-types';
import { Button } from '../../core/button';
import { Link } from '@tanstack/react-router';
import { Check, ChevronLeft, ChevronRight } from 'lucide-react';
import { ContentDescription } from '../components/shared/content-description';
import { ExerciseSubmissionHistory } from '../components/shared/exercise-submission-history';

interface ClassBlockListProps {
  blocks: StudentBlock[];
  latestSubmissionsByExerciseId: Record<string, LatestSubmissionResult>;
  submissionHistoriesByExerciseId?: Record<string, ExerciseSubmissionRecord[]>;
  renderContent: (content: StudentContent, options?: RenderContentOptions) => ReactNode;
  isFiltered: boolean;
  isMissingBlock: boolean;
  requestedBlockLabel?: number;
  scrollProgress: number;
  mode?: 'student' | 'teacher';
  /** Sections the teacher marked as homework for the reader's class session(s). */
  homeworkBlockIds?: string[];
  navigation?: {
    previousIdx?: number;
    nextIdx?: number;
    normalizedBlockIdx?: number;
    classId: string;
    finalAction?: {
      to?: string;
      label: string;
      isLoading?: boolean;
    };
  };
}

export function ClassBlockList({
  blocks,
  latestSubmissionsByExerciseId,
  submissionHistoriesByExerciseId,
  renderContent,
  isFiltered,
  isMissingBlock,
  requestedBlockLabel,
  scrollProgress,
  mode = 'student',
  homeworkBlockIds = [],
  navigation,
}: ClassBlockListProps) {
  const { t } = useTranslation('p2e');
  const sortedBlocks = useMemo(
    () => blocks.slice().sort((a, b) => a.order - b.order),
    [blocks],
  );

  const groupedBlocks = useMemo(
    () =>
      sortedBlocks.map((block) => ({
        ...block,
        groupedContents: groupContentsByContinuity(block.contents),
      })),
    [sortedBlocks],
  );

  if (sortedBlocks.length === 0 && !isFiltered) {
    return (
      <div
        className={cn(
          'rounded-lg border border-dashed border-border p-10 text-center text-muted-foreground transition-all duration-300',
          scrollProgress > 0.5 ? 'bg-card/90 backdrop-blur-sm' : 'bg-card',
        )}
      >
        {t('class.emptyContent')}
      </div>
    );
  }

  if (isMissingBlock) {
    return (
      <div
        className={cn(
          'rounded-lg border border-dashed border-border p-10 text-center text-muted-foreground transition-all duration-300',
          scrollProgress > 0.5 ? 'bg-card/90 backdrop-blur-sm' : 'bg-card',
        )}
      >
        {requestedBlockLabel ? t('class.blockNNotFound', { number: requestedBlockLabel }) : t('class.blockNotFound')}
      </div>
    );
  }

  const hasPrev = navigation?.previousIdx !== undefined;
  const hasNext = navigation?.nextIdx !== undefined;
  const finalAction = navigation?.finalAction;
  const hasFinalAction = Boolean(finalAction);
  const showFloatingMobileNav = Boolean(
    navigation && (hasPrev || hasNext || hasFinalAction) && scrollProgress > 0.85,
  );

  return (
    <>
      {groupedBlocks.map((block) => (
        <article
          key={block.id}
          className={cn(
            'space-y-6 rounded-xl border border-border p-6 shadow-sm transition-all duration-300',
            scrollProgress > 0.5 ? 'bg-card/95 backdrop-blur-sm' : 'bg-card',
          )}
        >
          <header className="space-y-2">
            <div className="flex items-baseline justify-between gap-4">
              <h2 className="text-2xl font-semibold">{block.name}</h2>
              {homeworkBlockIds.includes(block.id) && (
                <span
                  data-testid="homework-tag"
                  className="shrink-0 rounded-full bg-[#cf2e2e] px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-white"
                >
                  {t('zine.homework')}
                </span>
              )}
            </div>
            <ContentDescription text={block.description} className="text-base" />
          </header>

          <div className="space-y-6">
            {block.groupedContents.length === 0 ? (
              <div className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground" />
            ) : (
              block.groupedContents.map((group, groupIndex) => renderGroupedContent({
                blockId: block.id,
                group,
                groupIndex,
                latestSubmissionsByExerciseId,
                submissionHistoriesByExerciseId,
                renderContent,
                mode,
              }))
            )}
          </div>
        </article>
      ))}

      {navigation && (
        <>
          <div className="pointer-events-none sticky bottom-6 hidden justify-end md:flex">
            <div className="pointer-events-auto flex gap-3">
              <Button
                asChild
                size="sm"
                className="shadow-lg"
                disabled={!hasPrev}
                variant="red"
              >
                {hasPrev ? (
                  <Link to={`/classes/${navigation.classId}/block/${navigation.previousIdx}`}>
                    <ChevronLeft className="mr-2 h-4 w-4" />
                    {t('class.previous')}
                  </Link>
                ) : (
                  <span className="flex items-center">
                    <ChevronLeft className="mr-2 h-4 w-4" />
                    {t('class.previous')}
                  </span>
                )}
              </Button>
              {hasNext ? (
                <Button
                  asChild
                  variant="red"
                  size="sm"
                  className="shadow-lg"
                >
                  <Link to={`/classes/${navigation.classId}/block/${navigation.nextIdx}`}>
                    {t('class.next')}
                    <ChevronRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              ) : finalAction ? (
                finalAction.isLoading || !finalAction.to ? (
                  <Button
                    variant="red"
                    size="sm"
                    className="shadow-lg"
                    disabled
                    isLoading={finalAction.isLoading}
                  >
                    {finalAction.label}
                  </Button>
                ) : (
                  <Button
                    asChild
                    variant="red"
                    size="sm"
                    className="shadow-lg"
                  >
                    <Link to={finalAction.to}>
                      {finalAction.label}
                      <ChevronRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                )
              ) : null}
            </div>
          </div>

          {showFloatingMobileNav && (
            <div className="pointer-events-none fixed inset-x-0 top-1/2 z-40 -translate-y-1/2 px-4 md:hidden">
              <div className="flex items-center justify-between">
                {hasPrev ? (
                  <Button
                    asChild
                    variant="red"
                    size="icon"
                    className="pointer-events-auto h-11 w-11 rounded-full shadow-lg"
                  >
                    <Link to={`/classes/${navigation.classId}/block/${navigation.previousIdx}`}>
                      <span className="sr-only">{t('class.previousBlock')}</span>
                      <ChevronLeft className="h-5 w-5" />
                    </Link>
                  </Button>
                ) : (
                  <span className="w-11" />
                )}
                {hasNext ? (
                  <Button
                    asChild
                    variant="red"
                    size="icon"
                    className="pointer-events-auto h-11 w-11 rounded-full shadow-lg"
                  >
                    <Link to={`/classes/${navigation.classId}/block/${navigation.nextIdx}`}>
                      <span className="sr-only">{t('class.nextBlock')}</span>
                      <ChevronRight className="h-5 w-5" />
                    </Link>
                  </Button>
                ) : finalAction ? (
                  finalAction.isLoading || !finalAction.to ? (
                    <Button
                      variant="red"
                      size="icon"
                      className="pointer-events-auto h-11 w-11 rounded-full shadow-lg"
                      disabled
                      isLoading={finalAction.isLoading}
                    >
                      <span className="sr-only">{finalAction.label}</span>
                    </Button>
                  ) : (
                    <Button
                      asChild
                      variant="red"
                      size="icon"
                      className="pointer-events-auto h-11 w-11 rounded-full shadow-lg"
                    >
                      <Link to={finalAction.to}>
                        <span className="sr-only">{finalAction.label}</span>
                        <Check className="h-5 w-5" />
                      </Link>
                    </Button>
                  )
                ) : (
                  <span className="w-11" />
                )}
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
}

interface RenderGroupedContentParams {
  blockId: string;
  group: StudentContent[];
  groupIndex: number;
  latestSubmissionsByExerciseId: Record<string, LatestSubmissionResult>;
  submissionHistoriesByExerciseId?: Record<string, ExerciseSubmissionRecord[]>;
  renderContent: (content: StudentContent, options?: RenderContentOptions) => ReactNode;
  mode: 'student' | 'teacher';
}

function renderGroupedContent({
  blockId,
  group,
  groupIndex,
  latestSubmissionsByExerciseId,
  submissionHistoriesByExerciseId,
  renderContent,
  mode,
}: RenderGroupedContentParams) {
  const isTeacherView = mode === 'teacher';

  if (group.length === 1) {
    const content = group[0];
    const latestSubmission =
      content.type === ContentType.Exercise && content.exerciseId
        ? latestSubmissionsByExerciseId[content.exerciseId]
        : null;
    const history =
      isTeacherView && content.type === ContentType.Exercise && content.exerciseId
        ? submissionHistoriesByExerciseId?.[content.exerciseId] ?? []
        : undefined;

    const contentWithSubmission: StudentContent =
      content.type === ContentType.Exercise
        ? ({
            ...content,
            lastSubmission: latestSubmission?.submission ?? null,
            lastEvaluation: latestSubmission?.evaluation ?? null,
          } as StudentContent)
        : content;

    return (
      <div key={content.id} className="space-y-2">
        {content.type === ContentType.Exercise && latestSubmission && (
          <p className="text-xs text-muted-foreground">
            Last attempt #{latestSubmission.attemptNumber} · {new Date(latestSubmission.submittedAt).toLocaleString()}
          </p>
        )}
        {renderContent(contentWithSubmission)}
        {isTeacherView &&
          content.type === ContentType.Exercise &&
          history &&
          history.length > 0 && (
            <ExerciseSubmissionHistory
              content={contentWithSubmission as StudentExerciseContent}
              submissions={history}
              isTeacherView={isTeacherView}
            />
          )}
      </div>
    );
  }

  return (
    <div
      key={`${blockId}-${groupIndex}-${group[0]?.id ?? 'group'}`}
      className="overflow-hidden rounded-xl border border-border bg-card shadow-sm"
    >
      {group.map((content, itemIndex) => {
        const latestSubmission =
          content.type === ContentType.Exercise && content.exerciseId
            ? latestSubmissionsByExerciseId[content.exerciseId]
            : null;
        const history =
          isTeacherView && content.type === ContentType.Exercise && content.exerciseId
            ? submissionHistoriesByExerciseId?.[content.exerciseId] ?? []
            : undefined;

        const contentWithSubmission: StudentContent =
          content.type === ContentType.Exercise
            ? ({
                ...content,
                lastSubmission: latestSubmission?.submission ?? null,
                lastEvaluation: latestSubmission?.evaluation ?? null,
              } as StudentContent)
            : content;

        return (
          <div
            key={content.id}
            className={cn('space-y-2 p-4', itemIndex > 0 && 'border-t border-border/70')}
          >
            {content.type === ContentType.Exercise && latestSubmission && (
              <p className="text-xs text-muted-foreground">
                Last attempt #{latestSubmission.attemptNumber} · {new Date(latestSubmission.submittedAt).toLocaleString()}
              </p>
            )}
            {renderContent(contentWithSubmission, { variant: 'inline' })}
            {isTeacherView &&
              content.type === ContentType.Exercise &&
              history &&
              history.length > 0 && (
                <ExerciseSubmissionHistory
                  content={contentWithSubmission as StudentExerciseContent}
                  submissions={history}
                  isTeacherView={isTeacherView}
                />
              )}
          </div>
        );
      })}
    </div>
  );
}

function groupContentsByContinuity(contents: StudentContent[]): StudentContent[][] {
  const groups: StudentContent[][] = [];
  let currentGroup: StudentContent[] = [];

  contents.forEach((content) => {
    const hasTitle = typeof content.title === 'string' && content.title.trim().length > 0;
    const hasDescription = typeof content.description === 'string' && content.description.trim().length > 0;

    if (currentGroup.length === 0 || hasTitle || hasDescription) {
      currentGroup = [content];
      groups.push(currentGroup);
      return;
    }

    currentGroup.push(content);
  });

  return groups;
}

